<script lang="ts" setup>
import { ref } from "vue";
import type CWResource from "~~/types/CWResource";
import type CWTest from "~~/types/CWTest";

const props = defineProps({
    product: {
        type: Object,
    },
});

// Data
const config = useRuntimeConfig();

const cwResource = ref<CWResource | null>(null);
const cwTests = ref<CWTest[]>([]);
if (props.product?.coursewave_product_id) {
    const requestURL =
        config.public.STATAMIC_API + `/cw/resource?resource_id=${props.product?.coursewave_product_id}&with_tests=true`;

    // useFetch silently handle 404/500. So try-catch does not work.
    const { data } = await useFetch<any>(requestURL);

    const resources = data.value;
    if (resources && Array.isArray(resources) && resources.length > 0) {
        cwResource.value = resources[0] as CWResource;
        const testCount = (cwResource.value?.tests as CWTest[] | undefined)?.length ?? 0;
        if (testCount > 0) {
            const tests = (cwResource.value?.tests as CWTest[]).slice();
            cwTests.value = tests.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        }
    } else {
        console.warn("CourseWave resource fetch returned no data:", requestURL);
    }
}

// Functions
function hasPageSamples(pages: object) {
    let isSample = false;
    pages.forEach((page) => {
        if (page.is_sample) {
            isSample = true;
        }
    });
    return isSample;
}

function ebookPageIsSample(pageNum: number) {
    if (
        pageNum &&
        cwResource.value &&
        cwResource.value.ebook_sample_pages &&
        cwResource.value.ebook_sample_pages.length > 0
    ) {
        return cwResource.value.ebook_sample_pages.includes(pageNum);
    }
    return false;
}
</script>

<template>
    <div>
        <div v-if="cwResource && cwTests.length > 0">
            <h2 class="text-center text-2xl">CourseWave</h2>
            <div class="mt-6 hidden border-b border-gray-300 pb-1 lg:flex">
                <div class="flex-1"></div>
                <div class="flex w-2/6 flex-none text-center">
                    <div class="flex-1">Workbook</div>
                    <div class="flex-1">Sample Question</div>
                </div>
            </div>
            <div
                v-for="test in cwTests"
                :key="test.id"
                class="border-b border-gray-300 py-2 lg:flex"
            >
                <div class="flex-1 leading-tight">{{ test.title }}</div>
                <div class="flex flex-none flex-row-reverse pt-1 text-sm md:flex-row lg:w-2/6 lg:text-center">
                    <div class="flex-1">
                        <a
                            v-if="ebookPageIsSample(test.ebook_page_number)"
                            :href="`${config.public.CW_HOST}/samples/resources/${cwResource.id}/preview-eBook?
                                page=${test.ebook_page_number}`"
                            target="_blank"
                            class="text-abc-blue"
                        >
                            Workbook Preview
                        </a>
                    </div>
                    <div class="flex-1">
                        <a
                            v-if="test.sample_pages_count > 0"
                            :href="`${config.public.CW_HOST}/samples/resources/${cwResource.id}/tests/${test.id}`"
                            target="_blank"
                            class="text-abc-blue"
                        >
                            View Question
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-else-if="!cwResource"
            class="py-2 text-center"
        >
            Sorry, we couldn't find the related CourseWave product.
        </div>
        <div
            v-else
            class="py-2 text-center"
        >
            Sorry, there is currently no table of contents.
        </div>
    </div>
</template>

<style scoped></style>
